import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../i18n';
import { supabase } from '../lib/supabase';
import Runner from '../components/Runner';
import AppHeader from '../components/AppHeader';
import { getCacheData, setCacheData } from '../utils/dataCache';

interface Project {
  id: string;
  name: string;
  description: string;
  html_template: string;
  editor_data: {
    canvasWidth: number;
    canvasHeight: number;
    elements: any[];
  };
  created_at: string;
  updated_at: string;
}

const RunnerPage = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(false); // ✨ بدون loading في البداية
  const { user } = useAuth();
  const { t, dir } = useLanguage();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) {
      navigate('/signin');
      return;
    }
    fetchProjects();
  }, [user, navigate]);

  const fetchProjects = async (showLoadingIndicator: boolean = false) => {
    try {
      // 📦 محاولة تحميل البيانات من الـ cache أولاً
      const cacheKey = `runner_projects_${user?.id}`;
      const cachedProjects = getCacheData<Project[]>(cacheKey);
      
      if (cachedProjects && cachedProjects.length > 0) {
        console.log('✅ Loaded runner projects from cache');
        setProjects(cachedProjects);
        // تحديث البيانات في الخلفية دون إظهار loading
        showLoadingIndicator = false;
      } else {
        // 📥 أول مرة، إظهار loading
        console.log('📥 Loading runner projects for the first time...');
        showLoadingIndicator = true;
      }
      
      if (showLoadingIndicator) {
        setLoading(true);
      }

      const { data, error } = await supabase
        .from('projects')
        .select('id, name, description, html_template, editor_data, created_at, updated_at, cover, thumbnail_url')
        .eq('user_id', user?.id) // 🔒 Only show user's own projects
        .order('updated_at', { ascending: false });

      if (error) throw error;
      
      const freshProjects = data || [];
      setProjects(freshProjects);
      
      // 💾 حفظ في الـ cache
      setCacheData(cacheKey, freshProjects);
      
    } catch (err: any) {
      console.error('Error loading projects:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white text-xl">{t('common.loading')}</div>
      </div>
    );
  }

  // If no project selected, show project selection
  if (!selectedProject) {
    return (
      <div className="min-h-screen bg-black" dir={dir} style={{ overflow: 'auto', height: '100vh' }}>
        <AppHeader />
        
        {/* Sub-header */}
        <div className="bg-zinc-900/50 border-b border-zinc-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <h2 className="text-xl font-bold text-white">{t('pages.runner.selectProject')}</h2>
          </div>
        </div>

        {/* Projects List */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {projects.length === 0 ? (
            <div className="text-center py-12 md:py-16">
              <svg className="w-16 h-16 md:w-24 md:h-24 mx-auto text-gray-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h3 className="text-lg md:text-xl font-semibold text-white mb-2">{t('pages.runner.noProjects')}</h3>
              <p className="text-sm md:text-base text-gray-400 mb-6">{t('pages.runner.createFirst')}</p>
              <button
                onClick={() => navigate('/dashboard')}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
              >
                {t('pages.runner.goToProjects')}
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {projects.map((project) => (
                <button
                  key={project.id}
                  onClick={() => setSelectedProject(project)}
                  className="bg-zinc-900 border border-zinc-800 hover:border-blue-500 rounded-xl overflow-hidden transition-all text-right group flex"
                  style={{ padding: 0 }}
                >
                  {/* صورة المشروع على اليمين */}
                  <div 
                    className="flex-shrink-0 bg-zinc-800 flex items-center justify-center relative overflow-hidden"
                    style={{ width: 120, height: 120 }}
                  >
                    {(project as any).cover || (project as any).thumbnail_url ? (
                      <img 
                        src={(project as any).cover || (project as any).thumbnail_url} 
                        alt={project.name}
                        className="w-full h-full"
                        style={{ objectFit: 'contain' }}
                      />
                    ) : (
                      <svg className="w-8 h-8 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    )}
                  </div>
                  
                  {/* معلومات المشروع */}
                  <div className="flex-1 p-4 flex flex-col justify-between">
                    <div>
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="text-lg font-semibold text-white group-hover:text-blue-400 transition-colors">
                          {project.name}
                        </h3>
                        <svg className="w-6 h-6 text-gray-500 group-hover:text-blue-500 transition-colors flex-shrink-0 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      {project.description && (
                        <p className="text-sm text-gray-400 mb-2 line-clamp-2">{project.description}</p>
                      )}
                    </div>
                    <div className="text-xs text-gray-500">
                      {t('pages.runner.lastUpdated')}: {new Date(project.updated_at).toLocaleDateString('en-US')}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </main>
      </div>
    );
  }

  // If project selected, show Runner with template
  return (
    <div className="min-h-screen bg-black" dir={dir} style={{ overflow: 'auto', height: '100vh' }}>
      <AppHeader />
      
      {/* Sub-header with Back Button */}
      <div className="bg-zinc-900/50 border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSelectedProject(null)}
              className="text-gray-400 hover:text-white transition-colors"
              title={t('common.back')}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
            <div>
              <h2 className="text-xl font-bold text-white">{selectedProject.name}</h2>
              <p className="text-xs text-gray-400">{t('pages.runner.runningTemplate')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Runner Component */}
      <main className="min-h-[calc(100vh-140px)] flex items-center justify-center py-4 sm:py-8 overflow-y-auto w-full">
        <div className="w-full max-w-full px-2 sm:px-0">
          {selectedProject.html_template ? (
            <Runner 
              template={selectedProject.html_template} 
              canvasWidth={selectedProject.editor_data?.canvasWidth || 800}
              canvasHeight={selectedProject.editor_data?.canvasHeight || 600}
              defaultValues={selectedProject.editor_data?.elements || []}
              projectName={selectedProject.name}
            />
          ) : (
            <div className="text-center py-16">
              <p className="text-gray-400">{t('pages.runner.noTemplate')}</p>
              <p className="text-gray-500 text-sm mt-2">{t('pages.runner.editAndSave')}</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default RunnerPage;
